'use client';

import type { Currency } from '@shared/utils';
import { useGetMatch, useGetPrize, useGetRank, useGetWinners } from '..';
import type { ExtraGame, OddsEntry } from '../../types';

type UseMapPrizeTablesPowerPlayProps = {
  data: OddsEntry[];
  currency: Currency;
  namespace: [lotteryNamespace: string, generalNamespace: string];
  hideWinners: boolean;
  extraGames: ExtraGame[];
};

/**
 * Maps lottery odds data into a format suitable for displaying prize tables for Power Play.
 * Main feature is that it returns two prize tables, one for the main lottery and one for the extra game.
 *
 * @param data - The array of odds entries for the lottery and any extra games.
 * @param currency - The currency to display prize amounts in.
 * @param namespace - Tuple containing the lottery and general translation namespaces.
 * @param hideWinners - (Optional) Use 'true' to hide winners.
 * @param extraGames - The array of extra games.
 * @returns An array of objects, each representing a prize table for a lottery or extra game.
 */
function useMapPrizeTablesPowerPlay({ data, currency, namespace, hideWinners, extraGames }: UseMapPrizeTablesPowerPlayProps) {
  const [lottery, general] = namespace;
  const getRank = useGetRank(lottery);
  const getMatch = useGetMatch(lottery);
  const getWinners = useGetWinners(general);
  const { default: getPrize } = useGetPrize(general);

  const primaryOdds = data?.[0];
  if (!primaryOdds) {
    return [];
  }

  const tableVariants = [primaryOdds.lotteryId, extraGames?.[0]?.name].filter(variant => typeof variant !== 'undefined');

  return tableVariants.map((variant, index) => ({
    lotteryId: variant,
    data: Object.entries(primaryOdds.ranks ?? {}).map(([rank, conditions]) => {
      const isMainLottery = index === 0;
      const powerPlayRankOne = !isMainLottery && rank === '1';
      return {
        rank: getRank(Number(rank)),
        match: getMatch(conditions[0]!.matches),
        ...(hideWinners ? {} : { winners: !powerPlayRankOne ? getWinners(isMainLottery ? conditions[0]?.winners : conditions[0]?.additionalWinners) : '-' }),
        amount: !powerPlayRankOne ? getPrize(isMainLottery ? conditions[0]?.prize?.[currency] : conditions[0]?.additionalPrize?.[currency]) : '-',
      };
    }),
  }));
}

export { useMapPrizeTablesPowerPlay };
